import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { listMembersApi, createMemberApi, updateMemberApi, deleteMemberApi } from '../lib/api';

export default function OperatorMembers() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = adding, else = member obj
  const [form, setForm] = useState({ name: '', email: '', is_active: true });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listMembersApi();
      setMembers(res.data || []);
    } catch (err) {
      toast.error('Failed to load members');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', email: '', is_active: true });
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, email: m.email, is_active: m.is_active });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await updateMemberApi(editing.id, form);
        toast.success('Member updated');
      } else {
        await createMemberApi(form);
        toast.success('Member added');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save member');
    }
    setSubmitting(false);
  };

  const toggleActive = async (m) => {
    try {
      await updateMemberApi(m.id, { is_active: !m.is_active });
      load();
    } catch (err) {
      toast.error('Failed to update member');
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from members?`)) return;
    try {
      await deleteMemberApi(m.id);
      toast.success('Member removed');
      load();
    } catch (err) {
      toast.error('Failed to remove member');
    }
  };

  const activeCount = members.filter(m => m.is_active).length;

  return (
    <div className="min-h-screen bg-page-bg">
      {/* Header */}
      <div className="bg-white border-b border-divider sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/operator')}
            className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#EEF6C6' }}>
              <Users className="w-4 h-4" style={{ color: '#6F8C00' }} />
            </div>
            <h1 className="font-heading text-lg font-bold text-text-primary">Members</h1>
          </div>
          <Button
            data-testid="add-member-btn"
            onClick={openAdd}
            className="rounded-full text-white"
            style={{ backgroundColor: '#6F8C00' }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Member
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div
            className="rounded-2xl border px-4 py-3"
            style={{ backgroundColor: '#F7FBEA', borderColor: '#D9E6B5' }}
          >
            <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#6F8C00' }}>Active Members</p>
            <p className="font-heading text-2xl font-bold mt-0.5" style={{ color: '#6F8C00' }}>{activeCount}</p>
          </div>
          <div className="rounded-2xl border border-divider bg-white px-4 py-3">
            <p className="text-xs uppercase tracking-wider font-semibold text-text-secondary">Total</p>
            <p className="font-heading text-2xl font-bold mt-0.5 text-text-primary">{members.length}</p>
          </div>
        </div>

        {/* Info banner */}
        <div className="rounded-2xl border border-divider bg-white px-4 py-3 mb-4 text-sm text-text-secondary">
          <p>
            Members automatically receive discounted Training Package pricing (<span className="font-semibold text-text-primary">₱400/hr</span> instead of ₱450/hr) when they book with their verified email.
          </p>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-center text-text-secondary py-8">Loading…</p>
        ) : members.length === 0 ? (
          <div className="rounded-2xl border border-divider bg-white px-6 py-10 text-center">
            <Users className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">No members yet.</p>
            <p className="text-xs text-gray-400 mt-1">Click &ldquo;Add Member&rdquo; or add them directly from a booking.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-divider bg-white overflow-hidden">
            {members.map((m, idx) => (
              <div
                key={m.id}
                data-testid={`member-row-${m.id}`}
                className={`flex items-center gap-3 px-4 py-3 ${idx > 0 ? 'border-t border-divider' : ''} ${!m.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-text-primary truncate">{m.name}</p>
                  <p className="text-xs text-text-secondary truncate">{m.email}</p>
                </div>
                <button
                  data-testid={`member-toggle-${m.id}`}
                  onClick={() => toggleActive(m)}
                  className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  style={
                    m.is_active
                      ? { backgroundColor: '#EEF6C6', color: '#6F8C00' }
                      : { backgroundColor: '#F3F4F6', color: '#6B7280' }
                  }
                  title="Click to toggle"
                >
                  {m.is_active ? 'Active' : 'Inactive'}
                </button>
                <button
                  data-testid={`member-edit-${m.id}`}
                  onClick={() => openEdit(m)}
                  className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button
                  data-testid={`member-delete-${m.id}`}
                  onClick={() => remove(m)}
                  className="p-1.5 rounded text-gray-400 hover:text-status-expired hover:bg-status-expired-bg"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{editing ? 'Edit Member' : 'Add Member'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Name</Label>
              <Input
                data-testid="member-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Email</Label>
              <Input
                data-testid="member-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="member@example.com"
                className="mt-1"
              />
              <p className="text-xs text-gray-400 mt-1">Must match the email they use to book.</p>
            </div>
            {editing && (
              <label className="flex items-center gap-2 pt-2">
                <input
                  data-testid="member-active-checkbox"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4 rounded accent-[#6F8C00]"
                />
                <span className="text-sm text-text-primary">Active</span>
              </label>
            )}
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              data-testid="member-submit-btn"
              onClick={submit}
              disabled={submitting}
              className="rounded-full text-white"
              style={{ backgroundColor: '#6F8C00' }}
            >
              {submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Add Member')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
